import { type Dispatch, type SetStateAction, useCallback, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { v4 as uuid } from '@lukeed/uuid';
import axios from 'axios';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { trackFilterInteraction } from '@jsmdg/tracking';
import { type TypeAheadSuggestion } from '@jsmdg/yoshi';
import { PlaceApiStatus } from '../../shared/googlePlaces';
import { type ProductSearchLocationItem } from '../../shared/types';
import {
    trackLocationTypeInClicked,
    trackLocationTypeInError,
} from '../components/GiftBoxRelatedProducts/Filter.tracking';
import { SearchReducerActionType } from '../reducers/searchReducer';
import { DistanceBoundaryInKm } from '../types/enums/distanceBoundaryInKm';
import { type ProductDetailContext } from '../types/types';

enum TrackingErrorLabels {
    TECHNICAL_PROBLEM = 'TechnicalProblem',
    INVALID_LOCATION = 'InvalidLocation',
}

const messages = defineMessages({
    invalidLocation: {
        defaultMessage: 'Vertippt? Diesen Ort konnten wir leider nicht finden.',
    },
    technicalProblem: {
        defaultMessage:
            'Leider ist die Ortsfilterung aus technischen Gründen momentan nicht verfügbar.',
    },
});

function useLocationFilter({
    sessionToken,
    setSessionToken,
    onSubmit,
}: {
    sessionToken: string | null;
    onSubmit: (type: SearchReducerActionType, value?: ProductSearchLocationItem) => void;
    setSessionToken: Dispatch<string | null>;
    setSuggestionData: Dispatch<SetStateAction<TypeAheadSuggestion[]>>;
}): {
    getDetails: (id: string | number, title: string) => Promise<void>;
    getSuggestions: (title: string) => Promise<TypeAheadSuggestion[]>;
} {
    const intl = useIntl();
    const { getPlacesSuggestions, getPlacesDetail } = useFragmentContext<ProductDetailContext>();
    const geolocationError = useRef<{
        previousError: string | null;
        previousLocation: string | null;
    }>({
        previousError: null,
        previousLocation: null,
    });

    const geolocationErrorTrackingHandler = (errorLabel: string, errorLocation: string): void => {
        const { previousError, previousLocation } = geolocationError.current;
        if (
            !previousError ||
            errorLabel !== previousError ||
            (errorLabel === TrackingErrorLabels.INVALID_LOCATION &&
                !errorLocation.includes(previousLocation as string, 0))
        ) {
            geolocationError.current.previousError = errorLabel;
            geolocationError.current.previousLocation = errorLocation;
            trackFilterInteraction('Error', errorLabel);
        }
    };

    return {
        getSuggestions: useCallback(
            async (title: string) => {
                let token = sessionToken;
                if (!token) {
                    token = uuid();
                    setSessionToken(token);
                }

                try {
                    const rawLocationSuggestions = await getPlacesSuggestions(title, token);
                    return rawLocationSuggestions.predictions.map(locationSuggest => ({
                        title: locationSuggest.structured_formatting.main_text,
                        subTitle: locationSuggest.structured_formatting.secondary_text,
                        id: locationSuggest.place_id,
                        highlighted: false,
                    }));
                } catch (error) {
                    let message = intl.formatMessage(messages.technicalProblem);
                    let label = TrackingErrorLabels.TECHNICAL_PROBLEM;
                    if ((error as Error).message === PlaceApiStatus.ZERO_RESULTS) {
                        message = intl.formatMessage(messages.invalidLocation);
                        label = TrackingErrorLabels.INVALID_LOCATION;
                    }

                    geolocationErrorTrackingHandler(label, title);

                    throw new Error(message);
                }
            },
            [getPlacesSuggestions, intl, sessionToken, setSessionToken],
        ),
        getDetails: useCallback(
            async (id: string | number, title: string) => {
                try {
                    const result = await getPlacesDetail(String(id), sessionToken as string);

                    setSessionToken(null);

                    onSubmit(SearchReducerActionType.Location, {
                        lat: result.result.geometry.location.lat,
                        long: result.result.geometry.location.lng,
                        name: result.result.formatted_address,
                        distance: DistanceBoundaryInKm.DEFAULT,
                    });
                    await trackLocationTypeInClicked(
                        `${title}_${DistanceBoundaryInKm.DEFAULT}km`,
                        title,
                        'Location,distance',
                    );
                } catch (error) {
                    if (axios.isAxiosError(error)) {
                        const errorName = (error.response?.data as { name: string }).name;

                        if (errorName === 'InvalidLocationError') {
                            await trackLocationTypeInError(
                                TrackingErrorLabels.INVALID_LOCATION,
                                intl.formatMessage(messages.invalidLocation),
                            );
                            throw new Error(intl.formatMessage(messages.invalidLocation));
                        }
                    }
                }
            },
            [getPlacesDetail, intl, onSubmit, sessionToken, setSessionToken],
        ),
    };
}

export { useLocationFilter };
